import React, { useState, useMemo } from 'react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, ComposedChart, Line,
  ScatterChart, Scatter, RadarChart, PolarGrid, PolarAngleAxis,
  PolarRadiusAxis, Radar, PieChart, Pie, Cell
} from 'recharts';
import {
  Filter, Activity, Scissors, TrendingUp, Layers, Factory, DollarSign,
  Ruler, Scale, Globe, MapPin, Zap, Box, Users, Truck, RefreshCw,
  Droplet, FileText, AlertTriangle, BarChart2, Target, Award, ShoppingCart
} from 'lucide-react';

// ─── THEME ────────────────────────────────────────────────────────────────────
const T = {
  bg:'#F8F9FA', sidebar:'#1f1f1f', card:'#ffffff',
  textMain:'#212529', textSub:'#6C757D',
  primary:'#1565C0', success:'#2E7D32', warning:'#FF8F00',
  danger:'#C62828', purple:'#7B1FA2', teal:'#00897B',
  orange:'#ff7575', pink:'#ff44cc', border:'#DEE2E6', accent:'#00b884',
  cardShadow:'0 1px 3px rgba(0,0,0,0.05), 0 2px 8px rgba(0,0,0,0.04)',
  cardHover:'0 4px 20px rgba(0,0,0,0.10)',
};
const CC = ['#1565C0','#388E3C','#E65100','#C62828','#6A1B9A','#00897B','#ea580c','#db2777','#0ea5e9','#84cc16'];

// ─── DATA ENGINE ─────────────────────────────────────────────────────────────
const generateMockData = () => {
  const seasons=['SS24','AW24','SS25','AW25','SS26'];
  const brands=['Zara','H&M','Uniqlo','Gap','Next','Mango','Primark'];
  const suppliers=['Alpha Tex','Beta Fabrics','Gamma Sourcing','Delta Knits','Epsilon Apparels'];
  const regions=['Vietnam','Bangladesh','India','Turkey','China'];
  const products=['T-Shirt','Denim Pant','Woven Shirt','Jacket','Dress','Shorts'];
  const fabrics=['Cotton 100%','Poly/Cotton','Viscose','Denim 12oz','Linen Blend','Knit Jersey'];
  const grades=['A','B','C','D'];
  return Array.from({length:900},(_,i)=>{
    const season=seasons[i%seasons.length];
    const brand=brands[i%brands.length];
    const product=products[i%products.length];
    const region=regions[i%regions.length];
    const supplier=suppliers[i%suppliers.length];
    const fabric=fabrics[i%fabrics.length];
    const locationGrade=grades[i%grades.length];
    let base=6+Math.random()*3;
    if(product==='Jacket') base=19+Math.random()*3;
    if(product==='Denim Pant') base=12+Math.random()*2;
    if(product==='Dress') base=14+Math.random()*3;
    const fabricCost=base*(0.37+Math.random()*0.18);
    const cmCost=base*(0.17+Math.random()*0.14);
    const trimsCost=base*0.08+Math.random()*0.55;
    const ohCost=base*0.13+Math.random()*0.3;
    const totalFob=fabricCost+cmCost+trimsCost+ohCost+Math.random()*1.5;
    const smvVal=base*1.35+Math.random()*7;
    let smvGroup='0-10m';
    if(smvVal>10) smvGroup='10-20m';
    if(smvVal>20) smvGroup='20-30m';
    if(smvVal>30) smvGroup='30-40m';
    const efficiency=45+Math.random()*45;
    const onTime=60+Math.random()*40;
    const fabricWidth=44+Math.floor(Math.random()*20);
    const fabricGsm=120+Math.floor(Math.random()*120);
    const consumption=1.0+Math.random()*0.7;
    const rmIndex=88+Math.random()*38;
    const automationLevel=Math.floor(Math.random()*5)+1;
    const defects=Math.floor(Math.random()*6);
    const trimsWastagePct=parseFloat((Math.random()*6).toFixed(1));
    const samplePassRate=70+Math.random()*30;
    const placedQty=Math.floor(2000+Math.random()*12000);
    const cpm=cmCost/smvVal;
    const isImported=Math.random()>0.55?'Imported':'Local';
    const leadTime=30+Math.floor(Math.random()*60);
    const yieldLoss=parseFloat((2+Math.random()*8).toFixed(1));
    const ohPct=parseFloat(((ohCost/totalFob)*100).toFixed(1));
    const qcPassRate=parseFloat(samplePassRate.toFixed(1));
    const defectRate=parseFloat((defects*20+Math.random()*10).toFixed(1));
    const colorVariants=2+Math.floor(Math.random()*8);
    const moqCompliant=Math.random()>0.3?1:0;
    const freightCost=parseFloat((0.2+Math.random()*0.8).toFixed(2));
    return {
      id:i,season,brand,product,region,supplier,fabric,locationGrade,
      fabricCost,cmCost,trimsCost,ohCost,totalFob,smvVal,smvGroup,
      efficiency,onTime,fabricWidth,fabricGsm,consumption,rmIndex,
      automationLevel,defects,trimsWastagePct,samplePassRate,placedQty,cpm,
      isImported,leadTime,yieldLoss,ohPct,qcPassRate,defectRate,
      colorVariants,moqCompliant,freightCost
    };
  });
};

const mergeDbData=(db,mock)=>{
  if(!db?.mgmtStyles?.length&&!db?.costings?.length) return mock;
  const styles=db.mgmtStyles||[];
  const costings=db.costings||[];
  const logs=db.productionLogs||[];
  const dbRows=styles.map((style,i)=>{
    const m=mock[i%mock.length];
    const cd=costings.find(c=>c.styleId===style.id)?.data||{};
    const cm=cd.cm||{};
    const log=logs.find(l=>l.style===style.name)||{};
    return {...m,season:style.season||m.season,brand:style.brand||style.name||m.brand,
      product:style.description||m.product,region:style.buyer||m.region,
      fabricCost:parseFloat(cd.fabricCost||m.fabricCost),cmCost:parseFloat(cd.cmTotal||cm.cmTotal||m.cmCost),
      trimsCost:parseFloat(cd.trimsCost||m.trimsCost),ohCost:parseFloat(cd.ohCost||m.ohCost),
      totalFob:parseFloat(cd.fobTotal||m.totalFob),smvVal:parseFloat(cm.sam||m.smvVal),
      efficiency:parseFloat(cm.eff||log.efficiency||m.efficiency),
      fabricWidth:parseFloat(cd.fabricWidth||m.fabricWidth),
      consumption:parseFloat(cd.consumption||m.consumption),
      placedQty:parseInt(log.quantity||style.qty||m.placedQty),cpm:parseFloat(cm.cpm||m.cpm)};
  });
  const combined=[...dbRows];
  while(combined.length<400) combined.push(mock[combined.length%mock.length]);
  return combined;
};

// ─── COMPONENTS ──────────────────────────────────────────────────────────────
const KCard=({label,value,sub,icon:Icon,color,trend})=>(
  <div style={{background:T.card,borderRadius:10,padding:'17px 18px',boxShadow:T.cardShadow,borderLeft:`5px solid ${color}`,display:'flex',flexDirection:'column',gap:4,transition:'box-shadow 0.2s'}}
    onMouseEnter={e=>e.currentTarget.style.boxShadow=T.cardHover}
    onMouseLeave={e=>e.currentTarget.style.boxShadow=T.cardShadow}>
    <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
      <div style={{fontSize:9.5,fontWeight:700,color:T.textSub,letterSpacing:'0.07em',textTransform:'uppercase',lineHeight:1.4}}>{label}</div>
      {Icon&&<Icon size={15} color={color} strokeWidth={2.2}/>}
    </div>
    <div style={{fontSize:25,fontWeight:900,color:T.textMain,lineHeight:1.1}}>{value}</div>
    {sub&&<div style={{fontSize:10.5,color:trend==='up'?T.success:trend==='down'?T.danger:T.textSub,fontWeight:600,display:'flex',alignItems:'center',gap:3}}>
      {trend==='up'?'▲':trend==='down'?'▼':''} {sub}
    </div>}
  </div>
);

const Sec=({id,icon:Icon,title,color})=>(
  <div id={id} style={{display:'flex',alignItems:'center',gap:10,margin:'42px 0 16px',borderBottom:`3px solid ${color}`,paddingBottom:10,scrollMarginTop:20}}>
    {Icon&&<Icon size={19} color={color}/>}
    <h2 style={{fontSize:16,fontWeight:900,color:T.textMain,margin:0,letterSpacing:'-0.01em'}}>{title}</h2>
  </div>
);

const Card=({title,sub,icon:Icon,children,height=255,mb=16})=>(
  <div style={{background:T.card,borderRadius:10,padding:'17px 18px',boxShadow:T.cardShadow,display:'flex',flexDirection:'column',marginBottom:mb}}>
    <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:11}}>
      <div>
        <div style={{fontSize:12.5,fontWeight:800,color:T.textMain}}>{title}</div>
        {sub&&<div style={{fontSize:10.5,color:T.textSub,marginTop:2}}>{sub}</div>}
      </div>
      {Icon&&<Icon size={15} color={T.textSub}/>}
    </div>
    <div style={{height}}>{children}</div>
  </div>
);

const FSel=({label,value,options,onChange})=>(
  <div style={{display:'flex',flexDirection:'column',gap:3}}>
    <label style={{fontSize:9,fontWeight:800,color:T.textSub,textTransform:'uppercase',letterSpacing:'0.1em'}}>{label}</label>
    <select value={value} onChange={e=>onChange(e.target.value)} style={{padding:'5px 26px 5px 9px',borderRadius:6,border:`1.5px solid ${T.border}`,fontSize:11.5,color:T.textMain,background:'#F8F9FA',outline:'none',cursor:'pointer',minWidth:110,fontWeight:600,appearance:'none'}}>
      <option value="All">All {label}s</option>
      {options.map(o=><option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

const TT=({active,payload,label})=>{
  if(!active||!payload?.length) return null;
  return <div style={{background:'white',border:`1px solid ${T.border}`,borderRadius:8,padding:'7px 11px',boxShadow:T.cardShadow,fontSize:11}}>
    <div style={{fontWeight:800,marginBottom:5,color:T.textMain}}>{label}</div>
    {payload.map((p,i)=><div key={i} style={{color:p.color||T.textSub,fontWeight:600}}>{p.name}: <b>{typeof p.value==='number'?p.value.toFixed(2):p.value}</b></div>)}
  </div>;
};

const PBar=({value,max=100,color=T.primary})=>(
  <div style={{background:'#F4F6F8',borderRadius:4,height:6,width:'100%',overflow:'hidden'}}>
    <div style={{width:`${Math.min(100,(value/max)*100)}%`,height:'100%',background:color,borderRadius:4}}/>
  </div>
);

const Bdg=({ok,yes='✓ On Track',no='⚠ Review'})=>(
  <span style={{padding:'3px 8px',borderRadius:4,fontSize:10.5,fontWeight:700,background:ok?'#C8E6C9':'#FFCDD2',color:ok?T.success:T.danger}}>{ok?yes:no}</span>
);

const G=(cols,gap=14,mb=16)=>({display:'grid',gridTemplateColumns:`repeat(${cols},1fr)`,gap,marginBottom:mb});

const SECS=[
  {id:'program', icon:Activity,   label:'Program View',      color:T.primary},
  {id:'costing', icon:DollarSign, label:'CM & Costing',      color:T.success},
  {id:'fabric',  icon:Layers,     label:'Fabric & RM',       color:T.warning},
  {id:'trims',   icon:Box,        label:'Trims & Logistics',  color:T.purple},
  {id:'qc',      icon:Award,      label:'Quality & OTD',     color:T.teal},
  {id:'sourcing',icon:Globe,      label:'Sourcing',          color:T.orange},
  {id:'admin',   icon:Users,      label:'Admin & Scorecard', color:T.pink},
];

// ─── MAIN ─────────────────────────────────────────────────────────────────────
export default function Dashboard({db={}}){
  const [active,setActive]=useState('program');
  const [filters,setFilters]=useState({season:'All',brand:'All',region:'All',supplier:'All',product:'All'});
  const [src,setSrc]=useState('demo');
  const mock=useMemo(()=>generateMockData(),[]);
  const master=useMemo(()=>{
    const m=mergeDbData(db,mock);
    setSrc((db?.mgmtStyles?.length>0)?'live':'demo');
    return m;
  },[db,mock]);

  const opts=k=>[...new Set(master.map(d=>d[k]))].sort();
  const fd=useMemo(()=>master.filter(d=>
    (filters.season==='All'||d.season===filters.season)&&
    (filters.brand==='All'||d.brand===filters.brand)&&
    (filters.region==='All'||d.region===filters.region)&&
    (filters.supplier==='All'||d.supplier===filters.supplier)&&
    (filters.product==='All'||d.product===filters.product)
  ),[filters,master]);

  const setF=(k,v)=>setFilters(p=>({...p,[k]:v}));
  const reset=()=>setFilters({season:'All',brand:'All',region:'All',supplier:'All',product:'All'});
  const scrollTo=id=>{document.getElementById(id)?.scrollIntoView({behavior:'smooth'});setActive(id);};

  // ── AGGREGATES ──
  const kpi=useMemo(()=>{
    const n=fd.length||1;
    const tq=fd.reduce((a,b)=>a+(b.placedQty||0),0)||1;
    const wavg=k=>fd.reduce((a,b)=>a+(b[k]||0)*(b.placedQty||1),0)/tq;
    const avg=k=>fd.reduce((a,b)=>a+(b[k]||0),0)/n;
    const pct=k=>fd.filter(d=>d[k]).length/n*100;
    return {
      cpm:wavg('cpm'), smv:wavg('smvVal'), fob:wavg('totalFob'),
      fabricCost:avg('fabricCost'), cmCost:avg('cmCost'), trimsCost:avg('trimsCost'),
      ohCost:avg('ohCost'), efficiency:avg('efficiency'), onTime:avg('onTime'),
      leadTime:avg('leadTime'), fabricWidth:avg('fabricWidth'), fabricGsm:avg('fabricGsm'),
      consumption:avg('consumption'), yieldLoss:avg('yieldLoss'), rmIndex:avg('rmIndex'),
      ohPct:avg('ohPct'), qcPassRate:avg('qcPassRate'), defectRate:avg('defectRate'),
      trimsWaste:avg('trimsWastagePct'), colorVariants:avg('colorVariants'),
      moqCompliance:pct('moqCompliant'), freightCost:avg('freightCost'),
      totalStyles:new Set(fd.map(d=>d.brand+d.product)).size,
      totalQty:tq, count:n,
    };
  },[fd]);

  // ── CHART DATA ──
  const agg=(key,fields,accumFn)=>{
    const g={};
    fd.forEach(d=>{
      const k=d[key];
      if(!g[k]) g[k]={[key]:k,...fields};
      accumFn(g[k],d);
    });
    return Object.values(g);
  };

  const costStack=useMemo(()=>{
    const g=agg('season',{Fabric:0,CM:0,Trims:0,OH:0,n:0},(r,d)=>{r.Fabric+=d.fabricCost;r.CM+=d.cmCost;r.Trims+=d.trimsCost;r.OH+=d.ohCost;r.n++;});
    return g.map(r=>({season:r.season,Fabric:+(r.Fabric/r.n).toFixed(2),CM:+(r.CM/r.n).toFixed(2),Trims:+(r.Trims/r.n).toFixed(2),OH:+(r.OH/r.n).toFixed(2)})).sort((a,b)=>a.season.localeCompare(b.season));
  },[fd]);

  const effByRegion=useMemo(()=>{
    const g=agg('region',{eff:0,cpm:0,n:0},(r,d)=>{r.eff+=d.efficiency;r.cpm+=d.cpm;r.n++;});
    return g.map(r=>({region:r.region,'Efficiency %':+(r.eff/r.n).toFixed(1),'CPM $':+(r.cpm/r.n).toFixed(4)}));
  },[fd]);

  const otdBySeason=useMemo(()=>{
    const g=agg('season',{otd:0,lead:0,n:0},(r,d)=>{r.otd+=d.onTime;r.lead+=d.leadTime;r.n++;});
    return g.map(r=>({season:r.season,'On-Time %':+(r.otd/r.n).toFixed(1),'Lead Days':+(r.lead/r.n).toFixed(1)})).sort((a,b)=>a.season.localeCompare(b.season));
  },[fd]);

  const rmTrend=useMemo(()=>{
    const g=agg('season',{rm:0,fob:0,n:0},(r,d)=>{r.rm+=d.rmIndex;r.fob+=d.totalFob;r.n++;});
    return g.map(r=>({season:r.season,'RM Index':+(r.rm/r.n).toFixed(1),'FOB $':+(r.fob/r.n).toFixed(2)})).sort((a,b)=>a.season.localeCompare(b.season));
  },[fd]);

  const fobByBrand=useMemo(()=>{
    const g=agg('brand',{f:0,n:0},(r,d)=>{r.f+=d.totalFob;r.n++;});
    return g.map(r=>({brand:r.brand,'Avg FOB $':+(r.f/r.n).toFixed(2)})).sort((a,b)=>b['Avg FOB $']-a['Avg FOB $']);
  },[fd]);

  const smvCons=useMemo(()=>{
    const g=agg('smvGroup',{c:0,n:0},(r,d)=>{r.c+=d.consumption;r.n++;});
    return g.map(r=>({name:r.smvGroup,'Avg Cons(m)':+(r.c/r.n).toFixed(3)})).sort((a,b)=>a.name.localeCompare(b.name));
  },[fd]);

  const widthScatter=useMemo(()=>fd.slice(0,120).map(d=>({'Width(in)':d.fabricWidth,'Cons(m)':+d.consumption.toFixed(3)})),[fd]);
  const smvFobScatter=useMemo(()=>fd.slice(0,150).map(d=>({'SMV(m)':+d.smvVal.toFixed(1),'FOB $':+d.totalFob.toFixed(2)})),[fd]);
  const fobRmScatter=useMemo(()=>fd.slice(0,120).map(d=>({'RM Index':+d.rmIndex.toFixed(1),'FOB $':+d.totalFob.toFixed(2)})),[fd]);

  const productMix=useMemo(()=>{
    const g={};fd.forEach(d=>{g[d.product]=(g[d.product]||0)+d.placedQty;});
    return Object.entries(g).map(([name,value])=>({name,value})).sort((a,b)=>b.value-a.value);
  },[fd]);

  const yieldByFabric=useMemo(()=>{
    const g=agg('fabric',{y:0,n:0},(r,d)=>{r.y+=d.yieldLoss;r.n++;});
    return g.map(r=>({name:r.fabric,'Yield Loss %':+(r.y/r.n).toFixed(1)})).sort((a,b)=>b['Yield Loss %']-a['Yield Loss %']);
  },[fd]);

  const ohPctByBrand=useMemo(()=>{
    const g=agg('brand',{oh:0,n:0},(r,d)=>{r.oh+=d.ohPct;r.n++;});
    return g.map(r=>({brand:r.brand,'OH% FOB':+(r.oh/r.n).toFixed(1)})).sort((a,b)=>b['OH% FOB']-a['OH% FOB']);
  },[fd]);

  const autoVsCm=useMemo(()=>{
    const g=agg('automationLevel',{cm:0,fob:0,n:0},(r,d)=>{r.cm+=d.cmCost;r.fob+=d.totalFob;r.n++;});
    return g.map(r=>({grade:`Gr.${r.automationLevel}`,'Avg CM $':+(r.cm/r.n).toFixed(2),'Avg FOB $':+(r.fob/r.n).toFixed(2)}));
  },[fd]);

  const qcByRegion=useMemo(()=>{
    const g=agg('region',{qc:0,dr:0,n:0},(r,d)=>{r.qc+=d.qcPassRate;r.dr+=d.defectRate;r.n++;});
    return g.map(r=>({region:r.region,'QC Pass %':+(r.qc/r.n).toFixed(1),'Defect Rate':+(r.dr/r.n).toFixed(1)}));
  },[fd]);

  const trimsWasteByProduct=useMemo(()=>{
    const g=agg('product',{w:0,n:0},(r,d)=>{r.w+=d.trimsWastagePct;r.n++;});
    return g.map(r=>({name:r.product,'Wastage %':+(r.w/r.n).toFixed(1)})).sort((a,b)=>b['Wastage %']-a['Wastage %']);
  },[fd]);

  const colorByBrand=useMemo(()=>{
    const g=agg('brand',{cv:0,n:0},(r,d)=>{r.cv+=d.colorVariants;r.n++;});
    return g.map(r=>({brand:r.brand,'Avg Colors':+(r.cv/r.n).toFixed(1)})).sort((a,b)=>b['Avg Colors']-a['Avg Colors']);
  },[fd]);

  const moqBySupp=useMemo(()=>{
    const g=agg('supplier',{ok:0,n:0},(r,d)=>{r.ok+=d.moqCompliant;r.n++;});
    return g.map(r=>({supplier:r.supplier,'MOQ %':+(r.ok/r.n*100).toFixed(0)})).sort((a,b)=>b['MOQ %']-a['MOQ %']);
  },[fd]);

  const freightByRegion=useMemo(()=>{
    const g=agg('region',{f:0,n:0},(r,d)=>{r.f+=d.freightCost;r.n++;});
    return g.map(r=>({region:r.region,'Freight $':+(r.f/r.n).toFixed(2)})).sort((a,b)=>b['Freight $']-a['Freight $']);
  },[fd]);

  const importSplit=useMemo(()=>{
    const g={Imported:0,Local:0};fd.forEach(d=>{g[d.isImported]=(g[d.isImported]||0)+1;});
    return Object.entries(g).map(([name,value])=>({name,value}));
  },[fd]);

  const locGrade=useMemo(()=>{
    const g=agg('locationGrade',{f:0,n:0},(r,d)=>{r.f+=d.totalFob;r.n++;});
    return g.map(r=>({grade:`Zone ${r.locationGrade}`,'Avg FOB $':+(r.f/r.n).toFixed(2)})).sort((a,b)=>a.grade.localeCompare(b.grade));
  },[fd]);

  const suppLead=useMemo(()=>{
    const g={};
    fd.forEach(d=>{
      if(!g[d.supplier]) g[d.supplier]={supplier:d.supplier,region:d.region,eff:0,otd:0,dr:0,fob:0,n:0};
      g[d.supplier].eff+=d.efficiency;g[d.supplier].otd+=d.onTime;
      g[d.supplier].dr+=d.defectRate;g[d.supplier].fob+=d.totalFob;g[d.supplier].n++;
    });
    return Object.values(g).map(r=>({supplier:r.supplier,region:r.region,
      efficiency:+(r.eff/r.n).toFixed(1),otd:+(r.otd/r.n).toFixed(1),
      defectRate:+(r.dr/r.n).toFixed(1),avgFob:+(r.fob/r.n).toFixed(2)}))
      .sort((a,b)=>b.efficiency-a.efficiency).slice(0,6);
  },[fd]);

  const radarProg=[
    {subject:'CPM',     A:Math.round(100-kpi.cpm*1000)},
    {subject:'Efficiency',A:Math.round(kpi.efficiency)},
    {subject:'OTD',     A:Math.round(kpi.onTime)},
    {subject:'RM Idx',  A:Math.round(100-(kpi.rmIndex-88))},
    {subject:'QC Pass', A:Math.round(kpi.qcPassRate)},
    {subject:'Cons Idx',A:Math.round(100-kpi.consumption*50)},
  ];
  const radarSupp=[
    {subject:'Efficiency',A:Math.round(kpi.efficiency),B:70},
    {subject:'OTD',       A:Math.round(kpi.onTime),    B:85},
    {subject:'QC Pass',   A:Math.round(kpi.qcPassRate),B:90},
    {subject:'MOQ OK',    A:Math.round(kpi.moqCompliance),B:100},
    {subject:'Defect Idx',A:Math.round(100-kpi.defectRate),B:90},
    {subject:'Lead Idx',  A:Math.round(100-kpi.leadTime/90*100),B:60},
  ];

  const importPct=((importSplit.find(d=>d.name==='Imported')?.value||0)/fd.length*100).toFixed(1);

  // ── RENDER ──
  return (
    <div style={{display:'flex',height:'100vh',fontFamily:"'Inter','Inter','Segoe UI','Inter',system-ui,sans-serif",overflow:'hidden',background:T.bg}}>

      {/* SIDEBAR */}
      <aside style={{width:205,background:T.sidebar,color:'white',display:'flex',flexDirection:'column',flexShrink:0,overflowY:'auto'}}>
        <div style={{padding:'16px 14px 12px',fontWeight:900,fontSize:12.5,letterSpacing:'0.1em',borderBottom:'1px solid #1A2A3A',color:'#DEE2E6'}}>
          <div style={{fontSize:8,color:'#495057',marginBottom:3,letterSpacing:'0.15em'}}>NIRVIKALP 4.0</div>
          FAIRCM ANALYTICS
        </div>
        <div style={{padding:'7px 0 2px 13px',fontSize:8,color:'#495057',letterSpacing:'0.12em',marginTop:5}}>SECTIONS</div>
        {SECS.map(s=>{
          const on=active===s.id;
          return <div key={s.id} onClick={()=>scrollTo(s.id)} style={{padding:'9px 14px',cursor:'pointer',display:'flex',alignItems:'center',gap:8,fontSize:11,fontWeight:700,letterSpacing:'0.03em',textTransform:'uppercase',color:on?'white':'#ADB5BD',background:on?'rgba(37,99,235,0.22)':'transparent',borderLeft:`4px solid ${on?s.color:'transparent'}`,transition:'all 0.14s'}}>
            <s.icon size={12} color={on?s.color:'#6C757D'}/>{s.label}
          </div>;
        })}
        <div style={{flex:1}}/>
        <div style={{padding:12,borderTop:'1px solid #1A2A3A'}}>
          <div style={{fontSize:9,color:'#495057',fontWeight:700,marginBottom:4,textTransform:'uppercase',letterSpacing:'0.08em'}}>Data Source</div>
          <div style={{padding:'4px 8px',borderRadius:5,fontSize:10.5,fontWeight:700,background:src==='live'?'rgba(22,163,74,0.15)':'rgba(217,119,6,0.15)',color:src==='live'?T.success:T.warning,display:'flex',alignItems:'center',gap:5}}>
            <div style={{width:6,height:6,borderRadius:'50%',background:src==='live'?T.success:T.warning}}/>
            {src==='live'?'🟢 Live DB':'🟡 Demo Mode'}
          </div>
          <div style={{fontSize:9.5,color:'#495057',marginTop:4}}>{fd.length.toLocaleString()} records</div>
        </div>
      </aside>

      {/* MAIN */}
      <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden'}}>
        {/* Filter bar */}
        <div style={{background:'white',borderBottom:`1px solid ${T.border}`,padding:'9px 20px',display:'flex',flexWrap:'wrap',gap:12,alignItems:'flex-end',flexShrink:0}}>
          <div style={{display:'flex',alignItems:'center',gap:5}}>
            <Filter size={12} color={T.textSub}/>
            <span style={{fontSize:9.5,fontWeight:800,color:T.textSub,textTransform:'uppercase',letterSpacing:'0.08em'}}>FILTERS</span>
          </div>
          {Object.entries({season:'Season',brand:'Brand',region:'Region',supplier:'Supplier',product:'Product'}).map(([k,l])=>(
            <FSel key={k} label={l} value={filters[k]} options={opts(k)} onChange={v=>setF(k,v)}/>
          ))}
          <button onClick={reset} style={{padding:'5px 12px',borderRadius:6,border:`1.5px solid ${T.border}`,background:'white',fontSize:11,fontWeight:700,cursor:'pointer',color:T.textSub,display:'flex',alignItems:'center',gap:4,alignSelf:'flex-end'}}>
            <RefreshCw size={10}/> Reset
          </button>
        </div>

        {/* Content */}
        <div style={{flex:1,overflowY:'auto',padding:'18px 22px'}}>

          {/* ══ SECTION 1: PROGRAM VIEW ══ */}
          <Sec id="program" icon={Activity} title="PROGRAM VIEW · Executive KPI Summary (KPI 1-3, 7, 10-12)" color={T.primary}/>
          <div style={G(4)}>
            <KCard label="KPI 1 · W.Avg CPM"      value={`$${kpi.cpm.toFixed(4)}`}        sub="Cost per minute (qty-weighted)"    icon={DollarSign} color={T.primary}  trend="down"/>
            <KCard label="KPI 2 · W.Avg SMV"      value={`${kpi.smv.toFixed(1)}m`}         sub="Standard minute value"             icon={Ruler}      color={T.success}/>
            <KCard label="KPI 3 · W.Avg FOB"      value={`$${kpi.fob.toFixed(2)}`}         sub="+2.1% vs last year"                icon={Scale}      color={T.warning} trend="up"/>
            <KCard label="KPI 7 · Avg Efficiency" value={`${kpi.efficiency.toFixed(1)}%`}  sub={kpi.efficiency>70?'On target':'Below 70% target'} icon={Zap} color={T.purple} trend={kpi.efficiency>70?'up':'down'}/>
          </div>
          <div style={G(4)}>
            <KCard label="KPI 10 · On-Time Del."  value={`${kpi.onTime.toFixed(1)}%`}      sub="Avg OTD across all suppliers"      icon={Truck}      color={T.teal}   trend={kpi.onTime>80?'up':'down'}/>
            <KCard label="KPI 10 · Avg Lead Time" value={`${kpi.leadTime.toFixed(0)}d`}    sub="Source to delivery (days)"         icon={Activity}   color={T.orange} trend="down"/>
            <KCard label="Total Placed Qty"        value={`${(kpi.totalQty/1000).toFixed(0)}K`} sub="Aggregate PO volume"          icon={ShoppingCart} color={T.accent}/>
            <KCard label="Total Style Combos"     value={kpi.totalStyles.toLocaleString()} sub="Unique brand-product styles"       icon={FileText}   color={T.pink}/>
          </div>

          <div style={G(2)}>
            <Card title="KPI 11 · RM Index vs FOB Trends" sub="Season-on-season correlation" icon={TrendingUp} height={240}>
              <ResponsiveContainer>
                <ComposedChart data={rmTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F4F6F8"/>
                  <XAxis dataKey="season" tick={{fontSize:10,fontWeight:600}}/>
                  <YAxis yAxisId="l" tick={{fontSize:9}}/>
                  <YAxis yAxisId="r" orientation="right" tick={{fontSize:9}}/>
                  <Tooltip content={<TT/>}/><Legend wrapperStyle={{fontSize:10}}/>
                  <Bar yAxisId="l" dataKey="RM Index" fill="#90CAF9" radius={[3,3,0,0]}/>
                  <Line yAxisId="r" type="monotone" dataKey="FOB $" stroke={T.danger} strokeWidth={3} dot={{r:4}}/>
                </ComposedChart>
              </ResponsiveContainer>
            </Card>
            <Card title="KPI 12 · Avg FOB by Brand" sub="Brand-level price comparison" icon={BarChart2} height={240}>
              <ResponsiveContainer>
                <BarChart data={fobByBrand} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#F4F6F8"/>
                  <XAxis type="number" tick={{fontSize:9}}/>
                  <YAxis dataKey="brand" type="category" width={58} tick={{fontSize:10,fontWeight:600}}/>
                  <Tooltip content={<TT/>}/>
                  <Bar dataKey="Avg FOB $" radius={[0,3,3,0]}>{fobByBrand.map((_,i)=><Cell key={i} fill={CC[i%CC.length]}/>)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </div>

          <Card title="KPI 10 · On-Time Delivery % & Lead Time by Season" sub="Delivery reliability trend" icon={Truck} height={220} mb={0}>
            <ResponsiveContainer>
              <ComposedChart data={otdBySeason}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F4F6F8"/>
                <XAxis dataKey="season" tick={{fontSize:10,fontWeight:600}}/>
                <YAxis yAxisId="l" domain={[0,100]} tick={{fontSize:9}}/>
                <YAxis yAxisId="r" orientation="right" tick={{fontSize:9}}/>
                <Tooltip content={<TT/>}/><Legend wrapperStyle={{fontSize:10}}/>
                <Area yAxisId="l" type="monotone" dataKey="On-Time %" fill="#90CAF9" stroke={T.primary} strokeWidth={2} fillOpacity={0.35}/>
                <Line yAxisId="r" type="monotone" dataKey="Lead Days" stroke={T.orange} strokeWidth={3} dot={{r:4}}/>
              </ComposedChart>
            </ResponsiveContainer>
          </Card>

          {/* ══ SECTION 2: COSTING ══ */}
          <Sec id="costing" icon={DollarSign} title="CM & COSTING · KPI 4-9, 18-19, 22" color={T.success}/>
          <div style={G(4)}>
            <KCard label="KPI 5 · Avg CM Cost"  value={`$${kpi.cmCost.toFixed(2)}`}    sub="Cut-Make per garment"      icon={Scissors} color={T.success}/>
            <KCard label="KPI 6 · Avg Trims"    value={`$${kpi.trimsCost.toFixed(2)}`} sub="Trims & accessories avg"   icon={Box}      color={T.warning}/>
            <KCard label="KPI 8 · Avg OH Cost"  value={`$${kpi.ohCost.toFixed(2)}`}    sub="Overhead per unit"         icon={Factory}  color={T.danger}/>
            <KCard label="KPI 19 · OH% of FOB"  value={`${kpi.ohPct.toFixed(1)}%`}     sub="Overhead cost share"       icon={Target}   color={T.purple} trend={kpi.ohPct<15?'up':'down'}/>
          </div>
          <div style={G(2)}>
            <Card title="KPI 4 · Costing Block Breakdown" sub="Season-on-season cost comparison" icon={DollarSign} height={250}>
              <ResponsiveContainer>
                <BarChart data={costStack}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F4F6F8"/>
                  <XAxis dataKey="season" tick={{fontSize:10,fontWeight:600}}/>
                  <YAxis tick={{fontSize:9}}/>
                  <Tooltip content={<TT/>}/><Legend wrapperStyle={{fontSize:10}}/>
                  <Bar dataKey="Fabric" stackId="a" fill="#90CAF9"/>
                  <Bar dataKey="CM"     stackId="a" fill="#86efac"/>
                  <Bar dataKey="Trims"  stackId="a" fill="#fde68a"/>
                  <Bar dataKey="OH"     stackId="a" fill="#fca5a5" radius={[3,3,0,0]}/>
                </BarChart>
              </ResponsiveContainer>
            </Card>
            <Card title="KPI 18 · SMV vs FOB Correlation" sub="Price sensitivity — complexity drives cost" icon={Scale} height={250}>
              <ResponsiveContainer>
                <ScatterChart>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F4F6F8"/>
                  <XAxis dataKey="SMV(m)"  name="SMV"  tick={{fontSize:9}} label={{value:'SMV (min)',position:'insideBottom',fontSize:9,offset:-2}}/>
                  <YAxis dataKey="FOB $"   name="FOB"  tick={{fontSize:9}} label={{value:'FOB ($)',angle:-90,position:'insideLeft',fontSize:9}}/>
                  <Tooltip cursor={{strokeDasharray:'3 3'}} content={({active,payload})=>active&&payload?.length?<div style={{background:'white',border:`1px solid ${T.border}`,padding:'6px 10px',borderRadius:7,fontSize:10}}><div>SMV: <b>{payload[0]?.value}m</b></div><div>FOB: <b>${payload[1]?.value}</b></div></div>:null}/>
                  <Scatter data={smvFobScatter} fill={T.success} fillOpacity={0.45}/>
                </ScatterChart>
              </ResponsiveContainer>
            </Card>
          </div>
          <div style={G(2)}>
            <Card title="KPI 22 · Automation Level vs CM Share" sub="Cost reduction by automation grade" icon={Zap} height={235}>
              <ResponsiveContainer>
                <ComposedChart data={autoVsCm}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F4F6F8"/>
                  <XAxis dataKey="grade" tick={{fontSize:10,fontWeight:600}}/>
                  <YAxis tick={{fontSize:9}}/>
                  <Tooltip content={<TT/>}/><Legend wrapperStyle={{fontSize:10}}/>
                  <Bar dataKey="Avg CM $" fill="#a5b4fc" radius={[3,3,0,0]}/>
                  <Line type="monotone" dataKey="Avg FOB $" stroke={T.orange} strokeWidth={3} dot={{r:4}}/>
                </ComposedChart>
              </ResponsiveContainer>
            </Card>
            <Card title="KPI 7 & 9 · Efficiency % & CPM by Region" sub="Regional production performance" icon={MapPin} height={235}>
              <ResponsiveContainer>
                <ComposedChart data={effByRegion}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F4F6F8"/>
                  <XAxis dataKey="region" tick={{fontSize:9.5,fontWeight:600}}/>
                  <YAxis yAxisId="l" domain={[0,100]} tick={{fontSize:9}}/>
                  <YAxis yAxisId="r" orientation="right" tick={{fontSize:9}}/>
                  <Tooltip content={<TT/>}/><Legend wrapperStyle={{fontSize:10}}/>
                  <Bar yAxisId="l" dataKey="Efficiency %" fill="#86efac" radius={[3,3,0,0]}/>
                  <Line yAxisId="r" type="monotone" dataKey="CPM $" stroke={T.primary} strokeWidth={3} dot={{r:5}}/>
                </ComposedChart>
              </ResponsiveContainer>
            </Card>
          </div>
          <Card title="KPI 19 · Overhead % of FOB by Brand" sub="Brands with highest OH burden" icon={Factory} height={220} mb={0}>
            <ResponsiveContainer>
              <BarChart data={ohPctByBrand} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#F4F6F8"/>
                <XAxis type="number" tick={{fontSize:9}} unit="%"/>
                <YAxis dataKey="brand" type="category" width={55} tick={{fontSize:10,fontWeight:600}}/>
                <Tooltip content={<TT/>}/>
                <Bar dataKey="OH% FOB" radius={[0,3,3,0]}>{ohPctByBrand.map((_,i)=><Cell key={i} fill={CC[i%CC.length]}/>)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>

          {/* ══ SECTION 3: FABRIC & RM ══ */}
          <Sec id="fabric" icon={Layers} title="FABRIC & RAW MATERIALS · KPI 13-17" color={T.warning}/>
          <div style={G(4)}>
            <KCard label="KPI 13 · Avg Consumption"  value={`${kpi.consumption.toFixed(3)}m`}  sub="Fabric per garment"          icon={Droplet}      color={T.warning}/>
            <KCard label="KPI 14 · Avg Fabric Width" value={`${kpi.fabricWidth.toFixed(0)}"`}   sub="Avg width across program"    icon={Ruler}        color={T.orange}/>
            <KCard label="KPI 14 · Avg GSM"          value={`${kpi.fabricGsm.toFixed(0)}`}      sub="Fabric weight (gsm)"         icon={Scale}        color={T.pink}/>
            <KCard label="KPI 16 · Avg Yield Loss"   value={`${kpi.yieldLoss.toFixed(1)}%`}     sub="Cutting wastage"             icon={AlertTriangle} color={T.danger} trend="down"/>
          </div>
          <div style={G(2)}>
            <Card title="KPI 13 · SMV Group vs Avg Consumption" sub="Yield analysis by complexity" icon={Ruler} height={240}>
              <ResponsiveContainer>
                <BarChart data={smvCons}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F4F6F8"/>
                  <XAxis dataKey="name" tick={{fontSize:10,fontWeight:600}}/>
                  <YAxis tick={{fontSize:9}}/>
                  <Tooltip content={<TT/>}/>
                  <Bar dataKey="Avg Cons(m)" radius={[3,3,0,0]}>{smvCons.map((_,i)=><Cell key={i} fill={CC[i%CC.length]}/>)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
            <Card title="KPI 16 · Yield Loss % by Fabric Type" sub="Cutting waste by material" icon={Droplet} height={240}>
              <ResponsiveContainer>
                <BarChart data={yieldByFabric} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#F4F6F8"/>
                  <XAxis type="number" tick={{fontSize:9}} unit="%"/>
                  <YAxis dataKey="name" type="category" width={82} tick={{fontSize:9.5,fontWeight:600}}/>
                  <Tooltip content={<TT/>}/>
                  <Bar dataKey="Yield Loss %" radius={[0,3,3,0]}>{yieldByFabric.map((_,i)=><Cell key={i} fill={CC[i%CC.length]}/>)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </div>
          <div style={G(2)}>
            <Card title="KPI 14 · Fabric Width vs Consumption (Scatter)" sub="Wider fabrics reduce per-unit use" icon={Scale} height={235}>
              <ResponsiveContainer>
                <ScatterChart>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F4F6F8"/>
                  <XAxis dataKey="Width(in)" name="Width" tick={{fontSize:9}} label={{value:'Width (inches)',position:'insideBottom',fontSize:9,offset:-2}}/>
                  <YAxis dataKey="Cons(m)" name="Consumption" tick={{fontSize:9}} label={{value:'Consumption (m)',angle:-90,position:'insideLeft',fontSize:9}}/>
                  <Tooltip cursor={{strokeDasharray:'3 3'}} content={({active,payload})=>active&&payload?.length?<div style={{background:'white',border:`1px solid ${T.border}`,padding:'6px 10px',borderRadius:7,fontSize:10}}><div>Width: <b>{payload[0]?.value}"</b></div><div>Cons: <b>{payload[1]?.value}m</b></div></div>:null}/>
                  <Scatter data={widthScatter} fill={T.warning} fillOpacity={0.5}/>
                </ScatterChart>
              </ResponsiveContainer>
            </Card>
            <Card title="KPI 17 · FOB vs RM Price Index (Scatter)" sub="RM price impact on final FOB" icon={TrendingUp} height={235}>
              <ResponsiveContainer>
                <ScatterChart>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F4F6F8"/>
                  <XAxis dataKey="RM Index" name="RM Index" tick={{fontSize:9}} label={{value:'RM Index',position:'insideBottom',fontSize:9,offset:-2}}/>
                  <YAxis dataKey="FOB $" name="FOB $" tick={{fontSize:9}} label={{value:'FOB ($)',angle:-90,position:'insideLeft',fontSize:9}}/>
                  <Tooltip cursor={{strokeDasharray:'3 3'}} content={({active,payload})=>active&&payload?.length?<div style={{background:'white',border:`1px solid ${T.border}`,padding:'6px 10px',borderRadius:7,fontSize:10}}><div>RM Index: <b>{payload[0]?.value}</b></div><div>FOB: <b>${payload[1]?.value}</b></div></div>:null}/>
                  <Scatter data={fobRmScatter} fill={T.accent} fillOpacity={0.45}/>
                </ScatterChart>
              </ResponsiveContainer>
            </Card>
          </div>
          <Card title="KPI 15 · Product Mix by Placed Qty" sub="Volume distribution across product categories" icon={Box} height={230} mb={0}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={productMix} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={95} label={({name,percent})=>percent>0.06?`${name}: ${(percent*100).toFixed(0)}%`:''} labelLine={true} fontSize={11} fontWeight={700}>
                  {productMix.map((_,i)=><Cell key={i} fill={CC[i%CC.length]}/>)}
                </Pie>
                <Tooltip formatter={v=>v.toLocaleString()}/><Legend wrapperStyle={{fontSize:10}}/>
              </PieChart>
            </ResponsiveContainer>
          </Card>

          {/* ══ SECTION 4: TRIMS & LOGISTICS ══ */}
          <Sec id="trims" icon={Box} title="TRIMS, WASH & LOGISTICS · KPI 25-29, 31" color={T.purple}/>
          <div style={G(4)}>
            <KCard label="KPI 25 · Avg Trims Wastage" value={`${kpi.trimsWaste.toFixed(1)}%`}     sub="Avg trims wastage %"         icon={AlertTriangle} color={T.purple} trend="down"/>
            <KCard label="KPI 26 · Avg Color Variants" value={kpi.colorVariants.toFixed(1)}        sub="Colors per style"            icon={Layers}        color={T.pink}/>
            <KCard label="KPI 29 · Avg Freight Cost"   value={`$${kpi.freightCost.toFixed(2)}`}    sub="Per unit freight cost"       icon={Truck}         color={T.orange}/>
            <KCard label="KPI 28 · Imported Mix"       value={`${importPct}%`}                     sub="Imported material share"     icon={Globe}         color={T.teal} trend={parseFloat(importPct)>60?'down':'up'}/>
          </div>
          <div style={G(2)}>
            <Card title="KPI 25 · Trims Wastage % by Product" sub="Wastage reduction opportunities" icon={AlertTriangle} height={240}>
              <ResponsiveContainer>
                <BarChart data={trimsWasteByProduct}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F4F6F8"/>
                  <XAxis dataKey="name" tick={{fontSize:9.5,fontWeight:600}}/>
                  <YAxis tick={{fontSize:9}} unit="%"/>
                  <Tooltip content={<TT/>}/>
                  <Bar dataKey="Wastage %" radius={[3,3,0,0]}>{trimsWasteByProduct.map((_,i)=><Cell key={i} fill={CC[i%CC.length]}/>)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
            <Card title="KPI 29 · Freight Cost per Unit by Region" sub="Logistics cost comparison" icon={Truck} height={240}>
              <ResponsiveContainer>
                <BarChart data={freightByRegion}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F4F6F8"/>
                  <XAxis dataKey="region" tick={{fontSize:10,fontWeight:600}}/>
                  <YAxis tick={{fontSize:9}}/>
                  <Tooltip content={<TT/>}/>
                  <Bar dataKey="Freight $" radius={[3,3,0,0]}>{freightByRegion.map((_,i)=><Cell key={i} fill={CC[i%CC.length]}/>)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </div>
          <div style={G(2)}>
            <Card title="KPI 26 · Avg Color Variants by Brand" sub="Sampling complexity load" icon={Layers} height={225}>
              <ResponsiveContainer>
                <BarChart data={colorByBrand} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#F4F6F8"/>
                  <XAxis type="number" tick={{fontSize:9}}/>
                  <YAxis dataKey="brand" type="category" width={55} tick={{fontSize:10,fontWeight:600}}/>
                  <Tooltip content={<TT/>}/>
                  <Bar dataKey="Avg Colors" radius={[0,3,3,0]}>{colorByBrand.map((_,i)=><Cell key={i} fill={CC[i%CC.length]}/>)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
            <Card title="KPI 28 · Imported vs Local Material Split" sub="Supply chain composition" icon={Globe} height={225}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={importSplit} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={82} label={({name,percent})=>`${name}: ${(percent*100).toFixed(0)}%`} labelLine={true} fontSize={11} fontWeight={700}>
                    {importSplit.map((_,i)=><Cell key={i} fill={[T.primary,T.teal][i%2]}/>)}
                  </Pie>
                  <Tooltip/><Legend wrapperStyle={{fontSize:10}}/>
                </PieChart>
              </ResponsiveContainer>
            </Card>
          </div>
          <Card title="KPI 31 · Location Grade vs Avg FOB Impact" sub="Regional cost variance by production zone" icon={MapPin} height={220} mb={0}>
            <ResponsiveContainer>
              <BarChart data={locGrade}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F4F6F8"/>
                <XAxis dataKey="grade" tick={{fontSize:11,fontWeight:700}}/>
                <YAxis tick={{fontSize:9}}/>
                <Tooltip content={<TT/>}/>
                <Bar dataKey="Avg FOB $" radius={[3,3,0,0]}>{locGrade.map((_,i)=><Cell key={i} fill={CC[i%CC.length]}/>)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>

          {/* ══ SECTION 5: QUALITY & OTD ══ */}
          <Sec id="qc" icon={Award} title="QUALITY & OTD · KPI 23-24, 27, 10" color={T.teal}/>
          <div style={G(4)}>
            <KCard label="KPI 23 · QC Pass Rate"    value={`${kpi.qcPassRate.toFixed(1)}%`} sub="Sample quality pass rate"   icon={Award}         color={T.success} trend={kpi.qcPassRate>85?'up':'down'}/>
            <KCard label="KPI 24 · Avg Defect Rate" value={kpi.defectRate.toFixed(1)}        sub="Defects per 100 units"      icon={AlertTriangle} color={T.danger}  trend="down"/>
            <KCard label="KPI 27 · MOQ Compliance"  value={`${kpi.moqCompliance.toFixed(0)}%`} sub="Suppliers meeting MOQ"   icon={Target}        color={T.purple}  trend={kpi.moqCompliance>80?'up':'down'}/>
            <KCard label="KPI 10 · OTD Rate"        value={`${kpi.onTime.toFixed(1)}%`}     sub="On-time delivery avg"       icon={Truck}         color={T.teal}    trend={kpi.onTime>80?'up':'down'}/>
          </div>
          <div style={G(2)}>
            <Card title="KPI 23 & 24 · QC Pass % & Defect Rate by Region" sub="Regional quality benchmarks" icon={Award} height={245}>
              <ResponsiveContainer>
                <ComposedChart data={qcByRegion}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F4F6F8"/>
                  <XAxis dataKey="region" tick={{fontSize:9.5,fontWeight:600}}/>
                  <YAxis yAxisId="l" domain={[0,100]} tick={{fontSize:9}}/>
                  <YAxis yAxisId="r" orientation="right" tick={{fontSize:9}}/>
                  <Tooltip content={<TT/>}/><Legend wrapperStyle={{fontSize:10}}/>
                  <Bar yAxisId="l" dataKey="QC Pass %" fill="#86efac" radius={[3,3,0,0]}/>
                  <Line yAxisId="r" type="monotone" dataKey="Defect Rate" stroke={T.danger} strokeWidth={3} dot={{r:5}}/>
                </ComposedChart>
              </ResponsiveContainer>
            </Card>
            <Card title="KPI 27 · MOQ Compliance % by Supplier" sub="Minimum order quantity adherence" icon={Target} height={245}>
              <ResponsiveContainer>
                <BarChart data={moqBySupp} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#F4F6F8"/>
                  <XAxis type="number" tick={{fontSize:9}} unit="%"/>
                  <YAxis dataKey="supplier" type="category" width={90} tick={{fontSize:9.5,fontWeight:600}}/>
                  <Tooltip content={<TT/>}/>
                  <Bar dataKey="MOQ %" radius={[0,3,3,0]}>{moqBySupp.map((r,i)=><Cell key={i} fill={parseFloat(r['MOQ %'])>=80?T.success:parseFloat(r['MOQ %'])>=60?T.warning:T.danger}/>)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </div>

          {/* ══ SECTION 6: SOURCING ══ */}
          <Sec id="sourcing" icon={Globe} title="SOURCING · KPI 17, 20-21, 30" color={T.orange}/>
          <div style={G(4)}>
            <KCard label="KPI 21 · Active Suppliers" value={new Set(fd.map(d=>d.supplier)).size} sub="Unique vendors in program"  icon={Truck}      color={T.orange}/>
            <KCard label="Active Regions"            value={new Set(fd.map(d=>d.region)).size}   sub="Production geographies"     icon={Globe}      color={T.teal}/>
            <KCard label="KPI 17 · RM Index Avg"     value={kpi.rmIndex.toFixed(1)}              sub="Price vs baseline 100"      icon={TrendingUp} color={T.accent} trend={kpi.rmIndex>105?'down':'up'}/>
            <KCard label="Avg Top Supplier FOB"      value={`$${suppLead.length?(suppLead.reduce((a,b)=>a+b.avgFob,0)/suppLead.length).toFixed(2):'0.00'}`} sub="Top 6 suppliers avg" icon={DollarSign} color={T.pink}/>
          </div>
          <div style={G(2)}>
            <Card title="KPI 20 · Program Market Index Radar" sub="Multi-dimensional performance benchmark" icon={Globe} height={275}>
              <ResponsiveContainer>
                <RadarChart cx="50%" cy="50%" outerRadius="70%" data={radarProg}>
                  <PolarGrid stroke="#DEE2E6"/><PolarAngleAxis dataKey="subject" fontSize={11} fontWeight={700}/><PolarRadiusAxis fontSize={9}/>
                  <Radar name="Program" dataKey="A" stroke={T.primary} fill={T.primary} fillOpacity={0.32}/>
                  <Legend wrapperStyle={{fontSize:10}}/><Tooltip/>
                </RadarChart>
              </ResponsiveContainer>
            </Card>
            <Card title="KPI 30 · Market Index vs Supplier Index" sub="Supplier vs program benchmark" icon={Globe} height={275}>
              <ResponsiveContainer>
                <RadarChart cx="50%" cy="50%" outerRadius="70%" data={radarSupp}>
                  <PolarGrid stroke="#DEE2E6"/><PolarAngleAxis dataKey="subject" fontSize={11} fontWeight={700}/><PolarRadiusAxis fontSize={9}/>
                  <Radar name="Actual"    dataKey="A" stroke={T.orange}  fill={T.orange}  fillOpacity={0.28}/>
                  <Radar name="Benchmark" dataKey="B" stroke={T.success} fill={T.success} fillOpacity={0.15}/>
                  <Legend wrapperStyle={{fontSize:10}}/><Tooltip/>
                </RadarChart>
              </ResponsiveContainer>
            </Card>
          </div>

          {/* KPI 21 & 7 – Supplier Leaderboard */}
          <div style={{background:T.card,borderRadius:10,padding:'17px 18px',boxShadow:T.cardShadow,marginBottom:16}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:12}}>
              <div>
                <div style={{fontSize:12.5,fontWeight:800,color:T.textMain}}>KPI 21 & 7 · Strategic Supplier Leaderboard</div>
                <div style={{fontSize:10.5,color:T.textSub,marginTop:2}}>Ranked by efficiency — Delivery · QC · Defects</div>
              </div>
              <Truck size={15} color={T.textSub}/>
            </div>
            <table style={{width:'100%',fontSize:11.5,borderCollapse:'collapse'}}>
              <thead>
                <tr style={{background:'#0D1B2A'}}>
                  {['Rank','Supplier','Region','Efficiency','OTD %','Defect Rate','Avg FOB','Status'].map(h=>(
                    <th key={h} style={{padding:'9px 12px',textAlign:'left',fontSize:9.5,fontWeight:700,color:'#ADB5BD',textTransform:'uppercase',letterSpacing:'0.06em'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {suppLead.map((s,i)=>(
                  <tr key={i} style={{borderBottom:`1px solid ${T.border}`,background:i%2===0?'white':'#fafafa'}}>
                    <td style={{padding:'10px 12px',fontWeight:900,fontSize:15}}>{['🥇','🥈','🥉','4️⃣','5️⃣','6️⃣'][i]}</td>
                    <td style={{padding:'10px 12px',fontWeight:700}}>{s.supplier}</td>
                    <td style={{padding:'10px 12px',color:T.textSub}}>{s.region}</td>
                    <td style={{padding:'10px 12px'}}>
                      <div style={{display:'flex',alignItems:'center',gap:7}}>
                        <div style={{flex:1}}><PBar value={s.efficiency} color={s.efficiency>75?T.success:s.efficiency>65?T.warning:T.danger}/></div>
                        <span style={{fontWeight:700,minWidth:36}}>{s.efficiency}%</span>
                      </div>
                    </td>
                    <td style={{padding:'10px 12px',fontWeight:700,color:s.otd>80?T.success:T.warning}}>{s.otd}%</td>
                    <td style={{padding:'10px 12px',fontWeight:700,color:s.defectRate<30?T.success:s.defectRate<60?T.warning:T.danger}}>{s.defectRate}</td>
                    <td style={{padding:'10px 12px',fontWeight:700}}>${s.avgFob}</td>
                    <td style={{padding:'10px 12px'}}><Bdg ok={s.efficiency>72} yes="✓ Preferred" no={s.efficiency>60?'~ Monitor':'⚠ At Risk'}/></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ══ SECTION 7: ADMIN & SCORECARD ══ */}
          <Sec id="admin" icon={Users} title="ADMIN & SCORECARD · All 31 KPIs Master Table" color={T.pink}/>
          <div style={G(4)}>
            <KCard label="Total Records"  value={fd.length.toLocaleString()} sub="After all active filters"   icon={FileText} color={T.primary}/>
            <KCard label="Seasons Active" value={new Set(fd.map(d=>d.season)).size} sub="Distinct seasons"   icon={Activity} color={T.success}/>
            <KCard label="Brands"         value={new Set(fd.map(d=>d.brand)).size}  sub="Brand accounts"     icon={Award}    color={T.purple}/>
            <KCard label="Product Types"  value={new Set(fd.map(d=>d.product)).size} sub="Categories"        icon={Box}      color={T.warning}/>
          </div>

          {/* MASTER SCORECARD – ALL 31 KPIs */}
          <div style={{background:T.card,borderRadius:10,padding:'17px 18px',boxShadow:T.cardShadow,marginBottom:16}}>
            <div style={{fontSize:12.5,fontWeight:800,color:T.textMain,marginBottom:3}}>KPI Master Scorecard · All 31 KPIs</div>
            <div style={{fontSize:10.5,color:T.textSub,marginBottom:12}}>Program-level summary with benchmark status — benchmarks are indicative industry targets</div>
            <table style={{width:'100%',fontSize:11.5,borderCollapse:'collapse'}}>
              <thead>
                <tr style={{background:'#0D1B2A'}}>
                  {['KPI','Metric','Current Value','Benchmark','Section','Status'].map(h=>(
                    <th key={h} style={{padding:'9px 12px',textAlign:'left',fontSize:9.5,fontWeight:700,color:'#ADB5BD',textTransform:'uppercase',letterSpacing:'0.06em'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  {kpi:'KPI 1', metric:'W. Avg CPM',           value:`$${kpi.cpm.toFixed(4)}`,               bench:'< $0.0100',   ok:kpi.cpm<0.01,                    sec:'Program'},
                  {kpi:'KPI 2', metric:'W. Avg SMV',           value:`${kpi.smv.toFixed(1)}m`,               bench:'< 25m',       ok:kpi.smv<25,                      sec:'Program'},
                  {kpi:'KPI 3', metric:'W. Avg FOB',           value:`$${kpi.fob.toFixed(2)}`,               bench:'> $8.00',     ok:kpi.fob>8,                       sec:'Program'},
                  {kpi:'KPI 4', metric:'Avg Fabric Cost',      value:`$${kpi.fabricCost.toFixed(2)}`,        bench:'< $5.00',     ok:kpi.fabricCost<5,                sec:'Costing'},
                  {kpi:'KPI 5', metric:'Avg CM Cost',          value:`$${kpi.cmCost.toFixed(2)}`,            bench:'< $3.50',     ok:kpi.cmCost<3.5,                  sec:'Costing'},
                  {kpi:'KPI 6', metric:'Avg Trims Cost',       value:`$${kpi.trimsCost.toFixed(2)}`,         bench:'< $1.00',     ok:kpi.trimsCost<1,                 sec:'Costing'},
                  {kpi:'KPI 7', metric:'Avg Efficiency',       value:`${kpi.efficiency.toFixed(1)}%`,        bench:'> 70%',       ok:kpi.efficiency>70,               sec:'Program'},
                  {kpi:'KPI 8', metric:'Avg OH Cost',          value:`$${kpi.ohCost.toFixed(2)}`,            bench:'< $1.50',     ok:kpi.ohCost<1.5,                  sec:'Costing'},
                  {kpi:'KPI 9', metric:'CPM by Region',        value:`$${kpi.cpm.toFixed(4)}`,               bench:'< $0.0090',   ok:kpi.cpm<0.009,                   sec:'Costing'},
                  {kpi:'KPI 10',metric:'On-Time Delivery',     value:`${kpi.onTime.toFixed(1)}%`,            bench:'> 85%',       ok:kpi.onTime>85,                   sec:'Quality'},
                  {kpi:'KPI 11',metric:'RM Index',             value:kpi.rmIndex.toFixed(1),                 bench:'< 110',       ok:kpi.rmIndex<110,                 sec:'Program'},
                  {kpi:'KPI 12',metric:'FOB by Brand',         value:`$${kpi.fob.toFixed(2)} avg`,           bench:'> $8.00',     ok:kpi.fob>8,                       sec:'Program'},
                  {kpi:'KPI 13',metric:'Avg Consumption',      value:`${kpi.consumption.toFixed(3)}m`,       bench:'< 1.80m',     ok:kpi.consumption<1.8,             sec:'Fabric'},
                  {kpi:'KPI 14',metric:'Fabric Width',         value:`${kpi.fabricWidth.toFixed(0)}"`,       bench:'> 56"',       ok:kpi.fabricWidth>56,              sec:'Fabric'},
                  {kpi:'KPI 15',metric:'Product Mix',          value:`${new Set(fd.map(d=>d.product)).size} categories`, bench:'Balanced', ok:true,             sec:'Fabric'},
                  {kpi:'KPI 16',metric:'Yield Loss %',         value:`${kpi.yieldLoss.toFixed(1)}%`,         bench:'< 5%',        ok:kpi.yieldLoss<5,                 sec:'Fabric'},
                  {kpi:'KPI 17',metric:'RM Price Index',       value:kpi.rmIndex.toFixed(1),                 bench:'< 108',       ok:kpi.rmIndex<108,                 sec:'Sourcing'},
                  {kpi:'KPI 18',metric:'SMV vs FOB Corr.',     value:'Scatter chart ↗',                      bench:'R²> 0.7',     ok:true,                            sec:'Costing'},
                  {kpi:'KPI 19',metric:'OH % of FOB',          value:`${kpi.ohPct.toFixed(1)}%`,             bench:'< 15%',       ok:kpi.ohPct<15,                    sec:'Costing'},
                  {kpi:'KPI 20',metric:'Market Index Radar',   value:'6-axis radar ✓',                       bench:'All > 70',    ok:kpi.efficiency>70,               sec:'Sourcing'},
                  {kpi:'KPI 21',metric:'Active Suppliers',     value:new Set(fd.map(d=>d.supplier)).size,    bench:'≥ 3',         ok:new Set(fd.map(d=>d.supplier)).size>=3, sec:'Sourcing'},
                  {kpi:'KPI 22',metric:'Automation vs CM',     value:`$${kpi.cmCost.toFixed(2)} avg`,        bench:'< $3.00 Gr4', ok:kpi.cmCost<3,                    sec:'Costing'},
                  {kpi:'KPI 23',metric:'QC Pass Rate',         value:`${kpi.qcPassRate.toFixed(1)}%`,        bench:'> 85%',       ok:kpi.qcPassRate>85,               sec:'Quality'},
                  {kpi:'KPI 24',metric:'Defect Rate / 100u',   value:kpi.defectRate.toFixed(1),              bench:'< 50',        ok:kpi.defectRate<50,               sec:'Quality'},
                  {kpi:'KPI 25',metric:'Trims Wastage %',      value:`${kpi.trimsWaste.toFixed(1)}%`,        bench:'< 3%',        ok:kpi.trimsWaste<3,                sec:'Trims'},
                  {kpi:'KPI 26',metric:'Color Variants',       value:kpi.colorVariants.toFixed(1),           bench:'≤ 6',         ok:kpi.colorVariants<=6,            sec:'Trims'},
                  {kpi:'KPI 27',metric:'MOQ Compliance',       value:`${kpi.moqCompliance.toFixed(0)}%`,     bench:'> 80%',       ok:kpi.moqCompliance>80,            sec:'Quality'},
                  {kpi:'KPI 28',metric:'Imported Material %',  value:`${importPct}%`,                        bench:'< 60%',       ok:parseFloat(importPct)<60,        sec:'Trims'},
                  {kpi:'KPI 29',metric:'Freight Cost / Unit',  value:`$${kpi.freightCost.toFixed(2)}`,       bench:'< $0.60',     ok:kpi.freightCost<0.6,             sec:'Trims'},
                  {kpi:'KPI 30',metric:'Mkt vs Supplier Idx',  value:'Dual radar ✓',                         bench:'Aligned',     ok:true,                            sec:'Sourcing'},
                  {kpi:'KPI 31',metric:'Location Grade/FOB',   value:`$${kpi.fob.toFixed(2)} avg`,           bench:'Zone A best', ok:true,                            sec:'Trims'},
                ].map((row,i)=>(
                  <tr key={i} style={{borderBottom:`1px solid ${T.border}`,background:i%2===0?'white':'#fafafa'}}>
                    <td style={{padding:'8px 12px',fontWeight:900,color:T.primary,fontSize:10.5}}>{row.kpi}</td>
                    <td style={{padding:'8px 12px',fontWeight:600}}>{row.metric}</td>
                    <td style={{padding:'8px 12px',fontWeight:800,fontSize:13}}>{row.value}</td>
                    <td style={{padding:'8px 12px',color:T.textSub}}>{row.bench}</td>
                    <td style={{padding:'8px 12px'}}><span style={{padding:'2px 7px',borderRadius:3,fontSize:10,fontWeight:700,background:'#F4F6F8',color:T.textSub}}>{row.sec}</span></td>
                    <td style={{padding:'8px 12px'}}><Bdg ok={row.ok}/></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{height:60}}/>
        </div>
      </div>
    </div>
  );
}